type PricingRecord = {
  id: string;
  cycle_id: string;
  cycle_name: string;
  price: number;
  currency: string;
  formatted_price: string;
  interval: string;
};

type PlanRecord = {
  id: string;
  name: string;
  tier: string;
  description: string;
  is_current: boolean;
  features: string[];
  limits: Array<{ key: string; label: string; value: number }>;
  included_seats: number | null;
  max_seats: number | null;
  pricing: PricingRecord[];
};

type CurrentSubscriptionRecord = {
  id: string;
  plan_id: string | null;
  plan_name: string | null;
  plan_tier: string | null;
  cycle_id: string | null;
  price_id: string | null;
  display_status: string;
  status_label: string;
  next_billing_at: string | null;
  trial_expires_at: string | null;
  pending_cancellation_effective_at: string | null;
  seat_limit: number | null;
  seat_usage: number | null;
  trial_expired: boolean;
  can_pay_now: boolean;
  features: string[];
};

const plans: PlanRecord[] = [
  {
    id: 'plan-starter',
    name: 'Starter',
    tier: 'Individual',
    description: 'For early customer projects and lightweight usage.',
    is_current: false,
    features: ['Core API access', 'Email support', 'Usage dashboard'],
    limits: [
      { key: 'requests_per_month', label: 'Requests per month', value: 10000 },
      { key: 'projects', label: 'Projects', value: 3 },
    ],
    included_seats: 1,
    max_seats: 1,
    pricing: [
      {
        id: 'price-starter-monthly',
        cycle_id: 'cycle-monthly',
        cycle_name: 'Monthly',
        price: 2900,
        currency: 'USD',
        formatted_price: '$29',
        interval: 'per month',
      },
    ],
  },
  {
    id: 'plan-business-team',
    name: 'Business Team',
    tier: 'Team',
    description: 'For teams that need shared ownership and higher limits.',
    is_current: true,
    features: ['Everything in Starter', 'Shared team access', 'Priority support', 'Audit logs'],
    limits: [
      { key: 'requests_per_month', label: 'Requests per month', value: 100000 },
      { key: 'projects', label: 'Projects', value: 20 },
      { key: 'webhooks', label: 'Webhooks', value: 30 },
    ],
    included_seats: 5,
    max_seats: 5,
    pricing: [
      {
        id: 'price-business-monthly',
        cycle_id: 'cycle-monthly',
        cycle_name: 'Monthly',
        price: 9900,
        currency: 'USD',
        formatted_price: '$99',
        interval: 'per month',
      },
    ],
  },
  {
    id: 'plan-enterprise-team',
    name: 'Enterprise Team',
    tier: 'Team',
    description: 'For larger teams with advanced usage and governance.',
    is_current: false,
    features: ['Everything in Business', 'SLA', 'Dedicated support', 'Custom limits'],
    limits: [
      { key: 'requests_per_month', label: 'Requests per month', value: 500000 },
      { key: 'projects', label: 'Projects', value: 100 },
      { key: 'webhooks', label: 'Webhooks', value: 200 },
    ],
    included_seats: 15,
    max_seats: 15,
    pricing: [
      {
        id: 'price-enterprise-monthly',
        cycle_id: 'cycle-monthly',
        cycle_name: 'Monthly',
        price: 24900,
        currency: 'USD',
        formatted_price: '$249',
        interval: 'per month',
      },
    ],
  },
];

let currentSubscription: CurrentSubscriptionRecord = {
  id: 'sub-1',
  plan_id: 'plan-business-team',
  plan_name: 'Business Team',
  plan_tier: 'Team',
  cycle_id: 'cycle-monthly',
  price_id: 'price-business-monthly',
  display_status: 'trial_expired',
  status_label: 'trial_expired',
  next_billing_at: null,
  trial_expires_at: '2026-01-31T00:00:00Z',
  pending_cancellation_effective_at: null,
  seat_limit: 5,
  seat_usage: 4,
  trial_expired: true,
  can_pay_now: true,
  features: ['Everything in Starter', 'Shared team access', 'Priority support', 'Audit logs'],
};

let pendingIntentId: string | null = null;

function syncCurrentPlan(planId: string) {
  plans.forEach((plan) => {
    plan.is_current = plan.id === planId;
  });

  const nextPlan = plans.find((plan) => plan.id === planId);
  if (!nextPlan) {
    return;
  }

  currentSubscription = {
    ...currentSubscription,
    plan_id: nextPlan.id,
    plan_name: nextPlan.name,
    plan_tier: nextPlan.tier,
    cycle_id: nextPlan.pricing[0]?.cycle_id ?? currentSubscription.cycle_id,
    price_id: nextPlan.pricing[0]?.id ?? currentSubscription.price_id,
    seat_limit: nextPlan.max_seats,
    features: nextPlan.features,
  };
}

function getDowngradeOptions() {
  const options: string[] = [];
  const evaluations: Array<{ plan_id: string; eligible: boolean; reason_codes: string[] }> = [];
  const currentPrice = plans.find((plan) => plan.id === currentSubscription.plan_id)?.pricing[0]?.price ?? 0;

  plans.forEach((plan) => {
    if (plan.id === currentSubscription.plan_id) {
      return;
    }

    const planPrice = plan.pricing[0]?.price ?? 0;
    const isLowerTier = planPrice < currentPrice;
    const reasonCodes: string[] = [];
    let eligible = isLowerTier;

    if (!isLowerTier) {
      eligible = false;
      reasonCodes.push('not_a_downgrade');
    }

    if (
      typeof plan.max_seats === 'number' &&
      typeof currentSubscription.seat_usage === 'number' &&
      currentSubscription.seat_usage > plan.max_seats
    ) {
      eligible = false;
      reasonCodes.push('seat_limit_exceeded');
    }

    if (eligible) {
      options.push(plan.id);
    }

    evaluations.push({ plan_id: plan.id, eligible, reason_codes: reasonCodes });
  });

  return {
    options,
    evaluations,
    blocked_reason: options.length ? null : 'no_eligible_downgrade',
  };
}

function getUpgradeOptions() {
  const options: string[] = [];
  const evaluations: Array<{ plan_id: string; eligible: boolean; reason_codes: string[] }> = [];
  const currentPrice = plans.find((plan) => plan.id === currentSubscription.plan_id)?.pricing[0]?.price ?? 0;

  plans.forEach((plan) => {
    if (plan.id === currentSubscription.plan_id) {
      evaluations.push({ plan_id: plan.id, eligible: false, reason_codes: ['same_plan'] });
      return;
    }

    const planPrice = plan.pricing[0]?.price ?? 0;
    const isHigherTier = planPrice > currentPrice;
    const reasonCodes: string[] = [];
    const eligible = isHigherTier;

    if (!isHigherTier) {
      reasonCodes.push('not_an_upgrade');
    } else {
      options.push(plan.id);
    }

    evaluations.push({ plan_id: plan.id, eligible, reason_codes: reasonCodes });
  });

  return {
    current_plan_id: currentSubscription.plan_id,
    current_plan_name: currentSubscription.plan_name,
    options,
    evaluations,
    blocked_reason: options.length ? null : 'no_upgrade_candidates',
  };
}

export const customerPlanManagementOperations = {
  getPlanManagement: () => ({
    data: {
      attributes: {
        current_subscription: currentSubscription,
        plans,
        flags: {
          is_team_plan: true,
          has_pending_change: currentSubscription.display_status === 'pending_cancellation',
          can_resume: currentSubscription.display_status === 'cancelled',
          requires_reactivation_checkout: false,
        },
        transition_eligibility: {
          upgrade: getUpgradeOptions(),
          downgrade: getDowngradeOptions(),
        },
      },
    },
  }),

  getCheckoutContext: () => ({
    data: {
      attributes: {
        requires_payment: currentSubscription.trial_expired,
        stripe_intent_type: currentSubscription.trial_expired ? 'payment' : 'setup',
        stripe_publishable_key: 'pk_test_mock',
        stripe_account_id: null,
        plan: {
          id: currentSubscription.plan_id,
          name: currentSubscription.plan_name,
          product_id: 'mock-product',
          price: {
            currency: 'USD',
            base_amount: plans.find((plan) => plan.id === currentSubscription.plan_id)?.pricing[0]?.price
              ? (plans.find((plan) => plan.id === currentSubscription.plan_id)?.pricing[0]?.price ?? 0) / 100
              : null,
            recurring_amount: plans.find((plan) => plan.id === currentSubscription.plan_id)?.pricing[0]?.price
              ? (plans.find((plan) => plan.id === currentSubscription.plan_id)?.pricing[0]?.price ?? 0) / 100
              : null,
            billing_cycle_name: 'Monthly',
          },
        },
        payment_method: {
          status: 'none',
        },
        billing_address: {
          address_line_1: 'Main Street 12',
          city: 'San Francisco',
          postal_code: '94105',
          country: 'US',
        },
      },
    },
  }),

  createCheckoutIntent: () => {
    pendingIntentId = `cio_${Date.now()}`;
    return {
      data: {
        id: pendingIntentId,
        type: 'subscription_checkout_intent',
        attributes: {
          payment_required: true,
          stripe_intent_type: currentSubscription.trial_expired ? 'payment' : 'setup',
          client_secret: 'pi_mock_secret_secret_123',
          stripe_subscription_id: 'sub_mock_123',
          stripe_payment_intent_id: currentSubscription.trial_expired ? 'pi_mock_123' : null,
          stripe_setup_intent_id: currentSubscription.trial_expired ? null : 'seti_mock_123',
        },
      },
      status: 202,
    };
  },

  confirmCheckout: (data?: { purchase_intent_id?: string }) => {
    if (!data?.purchase_intent_id || data.purchase_intent_id !== pendingIntentId) {
      throw {
        response: {
          status: 422,
          data: {
            message: 'Invalid purchase intent.',
            errors: {
              purchase_intent_id: ['Invalid purchase intent.'],
            },
          },
        },
      };
    }

    currentSubscription = {
      ...currentSubscription,
      display_status: 'active',
      status_label: 'active',
      trial_expired: false,
      can_pay_now: false,
      next_billing_at: '2026-03-01T00:00:00Z',
      trial_expires_at: null,
    };

    return {
      data: {
        message: 'Subscription activated.',
        subscription: currentSubscription,
      },
    };
  },

  upgrade: (data?: { plan_id?: string }) => {
    const planId = data?.plan_id;
    if (!planId) {
      throw {
        response: {
          status: 422,
          data: {
            message: 'Plan is required.',
            errors: { plan_id: ['Plan is required.'] },
          },
        },
      };
    }

    if (currentSubscription.trial_expired) {
      throw {
        response: {
          status: 422,
          data: {
            message: 'Checkout required before changing plan.',
            errors: {
              subscription: ['Checkout required before changing plan.'],
            },
            meta: {
              code: 'checkout_required',
              required_action: 'complete_checkout',
              checkout_context_endpoint: '/api/v1/customer/subscription/checkout-context',
              checkout_intent_endpoint: '/api/v1/customer/subscription/checkout-intent',
            },
          },
        },
      };
    }

    syncCurrentPlan(planId);
    return {
      message: 'Plan upgraded.',
    };
  },

  downgrade: (data?: { plan_id?: string }) => {
    const planId = data?.plan_id;
    if (!planId) {
      throw {
        response: {
          status: 422,
          data: {
            message: 'Plan is required.',
            errors: { plan_id: ['Plan is required.'] },
          },
        },
      };
    }

    if (!getDowngradeOptions().options.includes(planId)) {
      throw {
        response: {
          status: 422,
          data: {
            message: 'Downgrade is not allowed for this plan.',
            errors: { subscription: ['Downgrade is not allowed for this plan.'] },
          },
        },
      };
    }

    syncCurrentPlan(planId);
    currentSubscription = {
      ...currentSubscription,
      display_status: 'active',
      status_label: 'active',
    };

    return {
      message: 'Downgrade scheduled.',
      meta: {
        active_until: '2026-03-01T00:00:00Z',
      },
    };
  },

  cancel: () => {
    currentSubscription = {
      ...currentSubscription,
      display_status: 'pending_cancellation',
      status_label: 'pending_cancellation',
      pending_cancellation_effective_at: '2026-03-01T00:00:00Z',
    };

    return {
      message: 'Cancellation scheduled.',
      meta: {
        active_until: '2026-03-01T00:00:00Z',
      },
    };
  },

  getDowngradeOptions: () => ({
    data: getDowngradeOptions(),
  }),
};
